import { useEffect, useRef, useState } from 'react'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Field, SelectField, Textarea } from '../../components/ui/controls'
import { Notice, Spinner } from '../../components/legal/LegalPresentation'
import { toast as message } from 'sonner'
import { Link } from 'react-router-dom'
import { getPersonalKycStatus, submitPersonalKyc } from '../../services/kyc'
import { getMerchant } from '../../services/merchant'
import { FileUploadItem, FileUrls, KycStatusData } from './KycShared'
import type { Merchant } from '../../types'
import MerchantAgreementConsent from '../../components/legal/MerchantAgreementConsent'
import { ApiError } from '../../services/api'
import type { AgreementSelection } from '../../services/merchantAgreement'

const NATIONALITY_OPTIONS = [
  { value: 'CN', label: '中國' },
  { value: 'HK', label: '香港' },
  { value: 'SG', label: '新加坡' },
  { value: 'US', label: '美國' },
  { value: 'RU', label: '俄羅斯' },
]

interface PersonalKycFormValues {
  nationality: string
  fullName: string
  gender: 'MALE' | 'FEMALE' | 'OTHER'
  birthDate: string
  documentType: 'PASSPORT' | 'ID_CARD' | 'DRIVER_LICENSE' | 'OTHER'
  documentNumber: string
  address: string
}

export default function KycSubmit() {
  const [personalKycStatus, setPersonalKycStatus] =
    useState<KycStatusData | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [fileUrls, setFileUrls] = useState<FileUrls>({})
  const [merchant, setMerchant] = useState<Merchant | null>(null)
  const [agreementOpen, setAgreementOpen] = useState(false)
  const [pendingValues, setPendingValues] = useState<PersonalKycFormValues>()
  const [agreementRefreshKey, setAgreementRefreshKey] = useState(0)
  const uploadGeneration = useRef(0)
  const [values, setValues] = useState<Record<string, string>>({})
  const selectedDocumentType = values.documentType
  const isPassport = selectedDocumentType === 'PASSPORT'
  const currentUploadGeneration = uploadGeneration.current

  useEffect(() => {
    Promise.all([getPersonalKycStatus(), getMerchant()])
      .then(([statusRes, merchantRes]) => {
        setPersonalKycStatus(statusRes.data)
        setMerchant(merchantRes.data)
      })
      .catch(() => message.error('認證資料載入失敗，請重新整理後重試'))
      .finally(() => setLoading(false))
  }, [])

  const handleFileUploaded = (key: keyof FileUrls, url: string) => {
    if (currentUploadGeneration !== uploadGeneration.current) return
    setFileUrls((prev) => ({ ...prev, [key]: url || undefined }))
  }

  const requiredPersonalFilesUploaded =
    !!fileUrls.personalDocumentFrontUrl &&
    !!fileUrls.personalDocumentBackUrl &&
    !!fileUrls.personalHeldIdPhotoUrl
  const merchantProfileComplete =
    !!merchant?.website?.trim() && !!merchant?.description?.trim()

  const handlePersonalSubmit = (values: PersonalKycFormValues) => {
    if (!values.nationality || !values.gender || !values.documentType) {
      message.error('請完成國籍、性別和證件類型')
      return
    }
    if (!merchantProfileComplete) {
      message.error('提交個人 KYC 前，請先在設定中補全網站和商戶描述')
      return
    }
    if (!requiredPersonalFilesUploaded) {
      message.error('請完成所有證件照片上傳')
      return
    }

    setPendingValues(values)
    setAgreementOpen(true)
  }

  const handleAgreementConfirm = async (
    agreementSelection: AgreementSelection,
  ) => {
    if (!pendingValues) return

    setSubmitting(true)
    try {
      await submitPersonalKyc({
        nationality: pendingValues.nationality,
        fullName: pendingValues.fullName,
        gender: pendingValues.gender,
        birthDate: pendingValues.birthDate,
        documentType: pendingValues.documentType,
        documentNumber: pendingValues.documentNumber,
        address: pendingValues.address,
        documentFrontUrl: fileUrls.personalDocumentFrontUrl!,
        documentBackUrl: fileUrls.personalDocumentBackUrl!,
        heldIdPhotoUrl: fileUrls.personalHeldIdPhotoUrl!,
        ...agreementSelection,
      })
      message.success('個人 KYC 已提交，請等待審核')
      setAgreementOpen(false)
      setPendingValues(undefined)
      getPersonalKycStatus().then((res) => setPersonalKycStatus(res.data))
      setValues({})
    } catch (error: unknown) {
      if (
        error instanceof ApiError &&
        [40911, 40912, 40913, 40914].includes(error.code ?? 0)
      ) {
        setAgreementRefreshKey((key) => key + 1)
        message.warning('協議或簽署者資料已更新，請重新閱讀並確認')
        return
      }
      message.error(error instanceof Error ? error.message : '提交失敗')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Card className="max-w-180 p-6">
        <Spinner />
      </Card>
    )
  }

  if (personalKycStatus?.status === 'APPROVED') {
    return (
      <div>
        <h1 className="text-xl font-semibold" style={{ marginBottom: 24 }}>
          個人 KYC
        </h1>
        <Notice title="個人 KYC 已通過"></Notice>
      </div>
    )
  }

  if (personalKycStatus?.status === 'PENDING') {
    return (
      <div>
        <h1 className="text-xl font-semibold" style={{ marginBottom: 24 }}>
          個人 KYC
        </h1>
        <Notice title="個人 KYC 審核中"></Notice>
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-xl font-semibold" style={{ marginBottom: 24 }}>
        個人 KYC
      </h1>

      {!merchantProfileComplete && (
        <Notice
          tone="warning"
          title="提交個人 KYC 前需補全商戶資訊"
          body="請先在賬戶設定中填寫網站和商戶描述。"
          action={
            <Link to="/dashboard/settings">
              <Button size="sm">前往設定</Button>
            </Link>
          }
          style={{ maxWidth: 720, marginBottom: 24 }}
        />
      )}

      <Card className="p-6" style={{ maxWidth: 720 }}>
        {personalKycStatus?.status === 'REJECTED' && (
          <Notice
            tone="error"
            title="個人 KYC 被拒絕"
            body={personalKycStatus.rejectReason}
            style={{ marginBottom: 16 }}
          />
        )}
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            handlePersonalSubmit(values as unknown as PersonalKycFormValues)
          }}
        >
          <Field label="國籍" htmlFor="nationality" required>
            <SelectField
              id="nationality"
              name="nationality"
              value={values.nationality || ''}
              onValueChange={(value) =>
                setValues((prev) => ({ ...prev, nationality: value }))
              }
              placeholder="請選擇國籍"
              options={NATIONALITY_OPTIONS}
            />
          </Field>
          <Field label="姓名" htmlFor="fullName" required>
            <Input
              id="fullName"
              name="fullName"
              value={values.fullName || ''}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, fullName: event.target.value }))
              }
              required
              placeholder="Full Name"
            />
          </Field>
          <Field label="性別" htmlFor="gender" required>
            <SelectField
              id="gender"
              name="gender"
              value={values.gender || ''}
              onValueChange={(value) =>
                setValues((prev) => ({ ...prev, gender: value }))
              }
              options={[
                { value: 'MALE', label: '男' },
                { value: 'FEMALE', label: '女' },
                { value: 'OTHER', label: '其他' },
              ]}
            />
          </Field>
          <Field label="出生日期" htmlFor="birthDate" required>
            <Input
              id="birthDate"
              name="birthDate"
              value={values.birthDate || ''}
              onChange={(event) =>
                setValues((prev) => ({
                  ...prev,
                  birthDate: event.target.value,
                }))
              }
              required
              type="date"
              style={{ width: '100%' }}
            />
          </Field>
          <Field label="證件類型" htmlFor="documentType" required>
            <SelectField
              id="documentType"
              name="documentType"
              value={values.documentType || ''}
              onValueChange={(value) => {
                uploadGeneration.current += 1
                setFileUrls({})
                setValues((prev) => ({ ...prev, documentType: value }))
              }}
              options={[
                { value: 'PASSPORT', label: '護照' },
                { value: 'ID_CARD', label: '身份證' },
                { value: 'DRIVER_LICENSE', label: '駕照' },
                { value: 'OTHER', label: '其他' },
              ]}
            />
          </Field>
          <Field label="證件號碼" htmlFor="documentNumber" required>
            <Input
              id="documentNumber"
              name="documentNumber"
              value={values.documentNumber || ''}
              onChange={(event) =>
                setValues((prev) => ({
                  ...prev,
                  documentNumber: event.target.value,
                }))
              }
              required
            />
          </Field>
          <Field label="地址" htmlFor="address" required>
            <Textarea
              id="address"
              name="address"
              value={values.address || ''}
              onChange={(event) =>
                setValues((prev) => ({ ...prev, address: event.target.value }))
              }
              required
              rows={2}
            />
          </Field>
          <FileUploadItem
            key={`${selectedDocumentType}-front`}
            label={isPassport ? '護照資料頁' : '證件正面'}
            fieldKey="personalDocumentFrontUrl"
            onUploaded={handleFileUploaded}
            uploaded={fileUrls.personalDocumentFrontUrl}
          />
          <FileUploadItem
            key={`${selectedDocumentType}-back`}
            label={isPassport ? '護照封面或補充頁' : '證件背面'}
            fieldKey="personalDocumentBackUrl"
            onUploaded={handleFileUploaded}
            uploaded={fileUrls.personalDocumentBackUrl}
          />
          <FileUploadItem
            key={`${selectedDocumentType}-held`}
            label={isPassport ? '本人手持護照照片' : '本人手持證件照片'}
            fieldKey="personalHeldIdPhotoUrl"
            onUploaded={handleFileUploaded}
            uploaded={fileUrls.personalHeldIdPhotoUrl}
          />

          <div>
            <Button
              variant="default"
              type="submit"
              disabled={
                !requiredPersonalFilesUploaded || !merchantProfileComplete
              }
            >
              提交
            </Button>
          </div>
        </form>
      </Card>

      <MerchantAgreementConsent
        open={agreementOpen}
        candidateLegalName={pendingValues?.fullName ?? ''}
        authorizedUserName={pendingValues?.fullName ?? ''}
        submissionType="PERSONAL"
        merchantAccountId={merchant?.id ?? ''}
        registeredEmail={merchant?.email ?? ''}
        refreshKey={agreementRefreshKey}
        confirming={submitting}
        onCancel={() => {
          if (submitting) return
          setAgreementOpen(false)
          setPendingValues(undefined)
        }}
        onConfirm={handleAgreementConfirm}
      />
    </div>
  )
}
